import { useCallback } from 'react'

import { useTranslation } from 'react-i18next'
import { useRouteLoaderData } from 'react-router'

import appDefaults from '~/app.defaults.json'
import { useAppConfig } from '~/hooks/useAppConfig'
import { AppLoaderData } from '~/types/app.ts'
import { ApiHubPlan, PORTAL_LICENSE_TYPE, PlanExpirationLevel, SWAGGERHUB_ROLE } from '~/types/portal.ts'
import { getPlanExpirationLevel, getTrialDaysLeft } from '~/utils/shared/trial'

export type UseApiHubPlanResult = Partial<Omit<ApiHubPlan, 'userUpgradeUrl' | 'adminUpgradeUrl'>> & {
  getUpgradeUrl: (role?: SWAGGERHUB_ROLE) => string,
  getUpgradeLabel: (role?: SWAGGERHUB_ROLE) => string,
  daysLeft: number,
  expirationLevel: PlanExpirationLevel,
  trial: boolean,
  daysAfterCutover: number,
  templatesEnabled: boolean,
}

export const UNLIMITED = 2147483647

export const useApiHubPlan = (): UseApiHubPlanResult => {
  const { t } = useTranslation()

  const { apiHubPlan, swaggerhubOrgId, license, cutoverDate } = useRouteLoaderData<AppLoaderData>('root') || {}
  const { platformUiUrl } = useAppConfig()

  const trial = license?.type === PORTAL_LICENSE_TYPE.TRIAL

  const daysLeft = apiHubPlan?.expiresAt ? getTrialDaysLeft(apiHubPlan.expiresAt) : UNLIMITED
  const expirationLevel = getPlanExpirationLevel(daysLeft)

  const formattedUrl = (url: string) => {
    if (!swaggerhubOrgId) {
      return ''
    }
    return `${platformUiUrl}${url.replace('{organizationId}', swaggerhubOrgId)}`
  }

  const getUpgradeUrl = useCallback((role) => {
    if (!apiHubPlan?.platformManaged) {
      return appDefaults.contactUrl
    }
    return role === SWAGGERHUB_ROLE.OWNER ?
      formattedUrl(appDefaults.apiHub.upgradeUrl) : formattedUrl(appDefaults.apiHub.contactAdminUrl)
  }, [swaggerhubOrgId, apiHubPlan?.platformManaged])

  const getUpgradeLabel = useCallback((role) => {
    if (!apiHubPlan?.platformManaged) {
      return t('entitlements.contactUs')
    }
    return role === SWAGGERHUB_ROLE.OWNER
      ? trial ? t('entitlements.managePlanLink') : t('entitlements.upgradeLink.admin')
      : t('entitlements.upgradeLink.user')
  }, [trial, apiHubPlan?.platformManaged])

  const getDaysAfterCutover = (cutoverDate: string): number => {
    const daysExpired = Math.round((new Date().getTime() - new Date(cutoverDate).getTime()) / (1000 * 3600 * 24))
    return daysExpired > 0 ? daysExpired : 0
  }

  const daysAfterCutover = cutoverDate ? getDaysAfterCutover(cutoverDate) : -1

  const templatesEnabled = !!apiHubPlan?.templateMeters?.limit && apiHubPlan.templateMeters.limit > 0

  return {
    ...apiHubPlan,
    trial,
    getUpgradeUrl,
    getUpgradeLabel,
    daysLeft,
    expirationLevel,
    daysAfterCutover,
    templatesEnabled,
  }
}